import { ApolloProvider } from '@apollo/client';

import {
  fingerprintApolloErrorEvent,
  sentryIgnoreErrors,
  shouldIgnoreSentryEvent,
} from '@motivo/guanyin/src/utils/sentryConfig';
import { initAuth, getIsHandlingAuthFailure } from '@motivo/styx/src/auth';
import { tagImpersonatedSentryEvent } from '@motivo/styx/src/auth-utils';
import { stripCallbackUrl } from '@motivo/styx/src/urlCallbackParams';
import * as Sentry from '@sentry/react';
import posthog, { type Properties } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import App from './App';
import apolloClient from './apollo/apolloClient';
import ErrorBoundary from '@motivo/guanyin/src/components/ErrorBoundary';

stripCallbackUrl();

function removeTokenFromUrl(url: string) {
  if (!URL.canParse(url, window.location.origin)) return url;

  const parsedUrl = new URL(url, window.location.origin);
  if (!parsedUrl.searchParams.has('token')) return url;
  parsedUrl.searchParams.delete('token');
  return url.startsWith('/')
    ? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    : parsedUrl.toString();
}

function removeTokenFromUrlProperties(properties: Properties) {
  return Object.fromEntries(
    Object.entries(properties).map(([name, value]) => [
      name,
      typeof value === 'string' ? removeTokenFromUrl(value) : value,
    ]),
  );
}

if (
  import.meta.env.VITE_POSTHOG_KEY &&
  (import.meta.env.MODE === 'production' || import.meta.env.VITE_DEV_ANALYTICS)
) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    autocapture: false,
    before_send: (captureResult) =>
      captureResult && {
        ...captureResult,
        properties: removeTokenFromUrlProperties(captureResult.properties),
        $set: captureResult.$set && removeTokenFromUrlProperties(captureResult.$set),
        $set_once: captureResult.$set_once && removeTokenFromUrlProperties(captureResult.$set_once),
      },
  });
}

if (import.meta.env.MODE === 'production') {
  Sentry.init({
    dsn: 'https://d77544da506741c19d9adc15c308e0fc@o446946.ingest.sentry.io/4504726291742720',
    environment: import.meta.env.MODE,
    ignoreErrors: sentryIgnoreErrors,
    beforeBreadcrumb(breadcrumb, hint) {
      const { category } = breadcrumb;
      if (category === 'xhr') return null;

      if (category === 'navigation') {
        return {
          ...breadcrumb,
          data: {
            ...breadcrumb.data,
            from: breadcrumb.data?.from && removeTokenFromUrl(breadcrumb.data.from),
            to: breadcrumb.data?.to && removeTokenFromUrl(breadcrumb.data.to),
          },
        };
      }

      if (category === 'ui.click') {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        const nodeName = hint.event.srcElement?.nodeName;

        if (['A', 'BUTTON'].includes(nodeName)) {
          return {
            ...breadcrumb,
            // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
            message: hint.event.srcElement?.innerText,
          };
        }
      } else if (category === 'fetch') {
        const {
          // @ts-expect-error TS(2339) FIXME: Property 'url' does not exist on type '{ [key: str... Remove this comment to see the full error message
          data: { url },
        } = breadcrumb;
        if (url.includes('/assets/') || url.endsWith('/graphql')) return null;
      }

      return breadcrumb;
    },
    beforeSend(event, hint) {
      if (shouldIgnoreSentryEvent(event, hint)) return null;

      const feedback = Sentry.getFeedback();
      if (feedback) {
        feedback.createWidget();
      }

      const requestUrl = event.request?.url;
      const scrubbedEvent = requestUrl
        ? { ...event, request: { ...event.request, url: removeTokenFromUrl(requestUrl) } }
        : event;

      return fingerprintApolloErrorEvent(tagImpersonatedSentryEvent(scrubbedEvent), hint);
    },
    normalizeDepth: 10,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        autoInject: false,
        showBranding: false,
        showName: false,
        showEmail: false,
        colorScheme: 'light',
        useSentryUser: {
          email: 'email',
          name: 'fullName',
        },
      }),
      Sentry.extraErrorDataIntegration({ depth: 10 }),
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
    ],
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });

  Sentry.getCurrentScope().setLevel('error');
}

initAuth({
  apiUrl: import.meta.env.VITE_API_URL,
  motivoProduct: 'astraea',
});

const container = document.getElementById('root');

const root = createRoot(container!);
root.render(
  <React.StrictMode>
    <ErrorBoundary getIsRecovering={getIsHandlingAuthFailure}>
      <ApolloProvider client={apolloClient}>
        <PostHogProvider client={posthog}>
          <Router>
            <App />
          </Router>
        </PostHogProvider>
      </ApolloProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

if (import.meta.env.MODE === 'production') {
  window.addEventListener('error', (errorEvent) => {
    if (/Failed to fetch dynamically imported module/.test(errorEvent.message)) {
      window.location.reload();
    }
  });
}
